import {
    ComponentProps,
    RefObject,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
    ColumnDef,
    getCoreRowModel,
    Header,
    Row,
    useReactTable,
} from '@tanstack/react-table'
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { payablesSummarySearchSchema } from '@/schemas'
import { ErrorState, SortingOrder, Table } from '../../../../components'
import {
    GetPayablesSummaryRequestParams,
    InvoiceDetailsResponse,
    Payable,
} from '../../../../hooks'

import { usePayablesSummaryLabelsMap } from './usePayablesSummaryLabelsMap'
import { getColumns } from './getColumns'
import { TableLoadingOverlay } from '../../../../components/common/Table/TableLoadingOverlay'
import { usePaginationConfig } from '../../../../hooks/usePaginationConfig/usePaginationConfig'
import { useBillingApi } from '../../../../hooks/useApi/useBillingApi/useBillingApi'
import { useAppNavigation } from '../../../../hooks/useAppNavigation/useAppNavigation'
import { ContentPlaceholder } from '@/components/molecules'

type PayablesSummaryTableContainerProps = {
    filters: GetPayablesSummaryRequestParams
}

export const PayablesSummaryTableContainer: React.FC<
    PayablesSummaryTableContainerProps
> = ({ filters }) => {
    const labelsMap = usePayablesSummaryLabelsMap()
    const navigate = useNavigate()

    const search = useSearch({ strict: false })
    const validatedSearch = payablesSummarySearchSchema.parse(search)
    const { sortBy, order, page, recordsPerPage } = validatedSearch

    const ref = useRef<HTMLDivElement>(null)

    const location = useLocation()

    const onTableColumnHeaderClick = useCallback(
        (header: Header<unknown, unknown>) => {
            const columnId = header.id
            const newOrder =
                sortBy !== columnId || order === 'asc' ? 'desc' : 'asc'
            const newSortBy = columnId

            void navigate({
                from: location.pathname as '/billing-and-payments/payables-summary',
                search: (prev) => {
                    const next = {
                        ...prev,
                        order: newOrder,
                        sortBy: newSortBy,
                    }
                    return next
                },
            })
        },
        [sortBy, order, navigate, location.pathname]
    )

    const onPaginationInfoChange = useCallback(
        ({
            page,
            recordsPerPage,
        }: {
            page: number
            recordsPerPage: number
        }) => {
            void navigate({
                from: location.pathname as '/billing-and-payments/payables-summary',
                search: (prev) => {
                    const next = {
                        ...prev,
                        page: page,
                        recordsPerPage: recordsPerPage,
                    }

                    return next
                },
            })
        },
        [navigate, location.pathname]
    )

    const getColumnSorting = useCallback(
        (header: Header<unknown, unknown>) => {
            if (sortBy === header.id) {
                return order as SortingOrder
            }
            return undefined
        },
        [order, sortBy]
    )

    const { usePayablesSummaryQuery } = useBillingApi()
    const params = {
        ...filters,
        sortBy,
        order,
        offset: (page - 1) * recordsPerPage,
        limit: recordsPerPage,
    }
    const { data, isSuccess, isLoading, isFetched, isFetching, isError } =
        usePayablesSummaryQuery(params)

    const columns = useMemo(
        () => getColumns(labelsMap, data?.items, ref),
        [labelsMap, data?.items, ref]
    )

    const pagination = usePaginationConfig({
        totalItems: data?.totalItems || 0,
        onPageChange: (newPage) => {
            onPaginationInfoChange({
                page: newPage,
                recordsPerPage: Number(pagination.recordsPerPage),
            })
        },
        onRecordsPerPageChange: (newRecordsPerPage) => {
            onPaginationInfoChange({
                page: 1,
                recordsPerPage: Number(newRecordsPerPage),
            })
        },
        siblingCount: 2,
        currentPage: page,
        recordsPerPage,
    })

    const emptyData = Array.from(new Array(50)).map(() => {
        const emptyPayable = {}
        return emptyPayable
    })

    if (isSuccess && data.items.length === 0) {
        return <PayablesSummaryEmptyState />
    }

    if (isError) {
        return <PayablesSummaryErrorState />
    }

    return (
        <PayablesSummaryTable
            columns={columns}
            onTableColumnHeaderClick={onTableColumnHeaderClick}
            getColumnSorting={getColumnSorting}
            data={data?.items ?? emptyData}
            isLoading={isFetching || isLoading || (!isFetched && !isFetching)}
            pagination={pagination}
            tableWrapperRef={ref}
        />
    )
}

const PayablesSummaryErrorState = () => {
    const { t } = useTranslation('translations')
    return (
        <ErrorState
            description={t(
                'billing_and_payments.payables_summary.report_error_state.description'
            )}
        />
    )
}

const PayablesSummaryEmptyState = () => {
    const { t } = useTranslation('translations')
    const title = t(
        'billing_and_payments.payables_summary.report_empty_state.title'
    )
    const description = t(
        'billing_and_payments.payables_summary.report_empty_state.description'
    )
    return (
        <ContentPlaceholder
            className="h-full"
            illustrationSrc="/assets/empty-state-illustration.svg"
            title={title}
            description={description}
            name="empty-state"
        />
    )
}

type PayablesSummaryTableProps = {
    data: Payable[] | Record<never, unknown>[]
    columns: ColumnDef<Payable>[]
    onTableColumnHeaderClick: ComponentProps<
        typeof Table
    >['onTableColumnHeaderClick']
    getColumnSorting: ComponentProps<typeof Table>['getColumnSorting']
    isLoading: boolean
    pagination: ComponentProps<typeof Table>['pagination']
    containerRef?: RefObject<HTMLDivElement>
    tableBodyRef?: RefObject<HTMLTableSectionElement>
    tableWrapperRef?: RefObject<HTMLDivElement>
    tableRef?: RefObject<HTMLTableElement>
}

export const PayablesSummaryTable: React.FC<PayablesSummaryTableProps> = (
    props
) => {
    const {
        data,
        columns,
        onTableColumnHeaderClick,
        getColumnSorting,
        isLoading,
        pagination,
        containerRef,
        tableBodyRef,
        tableWrapperRef,
        tableRef,
    } = props

    const [rowSelection, setRowSelection] = useState({})
    const { goToInvoice } = useAppNavigation()

    const table = useReactTable({
        data: data,
        columns: columns as ColumnDef<unknown>[],
        getCoreRowModel: getCoreRowModel(),
        state: {
            rowSelection,
        },
        enableRowSelection: true,
        onRowSelectionChange: (newRowsSelection) => {
            setRowSelection(newRowsSelection)
        },
    })

    const selectedInvoiceIds = table
        .getFilteredSelectedRowModel()
        .flatRows.map((row) => row.original)
        .map((invoice) => (invoice as InvoiceDetailsResponse).invoiceNo)

    const { t } = useTranslation('translations')
    const loadingReportTitle = t('common.loading_report')

    return (
        <div className="relative h-full" ref={containerRef}>
            <Table
                table={table}
                onTableColumnHeaderClick={onTableColumnHeaderClick}
                getColumnSorting={getColumnSorting}
                pagination={pagination}
                style="list"
                onRowClick={(row) => {
                    void goToInvoice((row as Row<Payable>).original.invoiceId)
                }}
                tableWrapperRef={tableWrapperRef}
                tableBodyRef={tableBodyRef}
                tableRef={tableRef}
                selectedInvoiceIds={selectedInvoiceIds}
            />

            <TableLoadingOverlay show={isLoading} title={loadingReportTitle} />
        </div>
    )
}
